/**
 * WbButton — Werkbank-knop.
 *
 * primary   inkt op wit, de ene hoofdactie
 * secondary wit met inktrand
 * link      tekstactie zonder vlak (Overslaan, Bericht aan kantoor)
 * danger    alleen voor Risico melden
 *
 * size lg = 64 px (StickyActionBar), md = 52 px. Volle breedte tenzij
 * fullWidth={false}. Zie tokens.md.
 */

import { type ButtonHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

export interface WbButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'link' | 'danger';
  size?: 'lg' | 'md';
  fullWidth?: boolean;
  isLoading?: boolean;
}

export const WbButton = forwardRef<HTMLButtonElement, WbButtonProps>(
  (
    { variant = 'primary', size = 'md', fullWidth = true, isLoading = false, className, disabled, children, type = 'button', ...props },
    ref
  ) => {
    const variants = {
      primary: 'bg-wb-inkt text-white border-wb border-wb-inkt active:bg-wb-inkt2',
      secondary: 'bg-wb-vlak text-wb-inkt border-wb border-wb-rand active:bg-wb-gedempt',
      link: 'bg-transparent text-wb-inkt2 underline underline-offset-4 border-0 px-2',
      danger: 'bg-wb-geld text-wb-geldink border-wb border-wb-geldink active:bg-red-200',
    };
    const sizes = {
      lg: 'min-h-taplg text-lg px-5',
      md: 'min-h-tap text-base px-4',
    };
    return (
      <button
        ref={ref}
        type={type}
        className={clsx(
          'wb-focus inline-flex items-center justify-center gap-2 rounded-wb font-wb-heading font-bold uppercase tracking-wide select-none',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          variants[variant],
          variant === 'link' ? 'min-h-tap text-base normal-case font-body font-semibold tracking-normal' : sizes[size],
          fullWidth && variant !== 'link' && 'w-full',
          className
        )}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <>
            <span className="w-5 h-5 border-[3px] border-current border-t-transparent rounded-full animate-spin" />
            <span>Bezig…</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

WbButton.displayName = 'WbButton';
